// Tipos TS espelhando schemas Pydantic do backend (app/modules/linkedin/schemas.py).
// Mantém sincronizado manualmente — se mudar lá, atualizar aqui.

import type { LinkedInTransition } from "@/lib/linkedin-transitions";

export type LinkedInJobStatus =
  | "pending"
  | "uploading"
  | "extracting"
  | "enriching"
  | "matching"
  | "awaiting_match"
  | "diffing"
  | "no_changes"
  | "review_ready"
  | "applied"
  | "failed"
  | "cancelled";

export interface ExperienceItem {
  empresa_nome: string;
  empresa_segmento_inferido: string | null;
  empresa_tipo: string | null;
  empresa_website: string | null;
  empresa_linkedin_url: string | null;
  empresa_cidade: string | null;
  empresa_pais: string | null;
  empresa_descricao: string | null;
  cargo_nome: string;
  senioridade_inferida: string | null;
  data_inicio: string;
  data_fim: string | null;
  is_atual: boolean;
  /**
   * true quando is_atual foi INFERIDO pela duração (o PDF não disse "Present"
   * explicitamente — comum em export em português, que mostra o mês de
   * exportação como data final). Pede confirmação humana na revisão.
   */
  is_atual_a_confirmar?: boolean;
  /**
   * Tipo de vínculo inferido pela IA: "empregado" (vínculo comum),
   * "freelance" (cargo declara atuação independente) ou "empresa_propria"
   * (dono do negócio). Null = sem evidência.
   */
  vinculo_tipo?: "empregado" | "freelance" | "empresa_propria" | null;
  /** true quando a IA reconhece a empresa por conhecimento público próprio. */
  empresa_reconhecida_publicamente?: boolean;
  /**
   * false quando o "empregador" não é empresa registrável (lista de clientes,
   * prêmio, cargo no campo empresa): não cria nem vincula entidade no apply.
   */
  empresa_registravel?: boolean;
  /** Justificativa curta da IA quando empresa_registravel=false. */
  empresa_registravel_motivo?: string | null;
  localizacao: string | null;
  descricao: string | null;
}

export interface SkillItem {
  nome: string;
  categoria_inferida: string | null;
  is_principal: boolean;
}

export interface FormacaoItem {
  instituicao: string;
  curso: string | null;
  periodo: string | null;
}

export interface IdiomaItem {
  nome: string;
  nivel: string | null;
}

export interface CarreiraObservacoes {
  tenure_medio_meses: number | null;
  tenure_maior_meses: number | null;
  tenure_atual_meses: number | null;
  n_experiencias_curtas: number | null;
  n_experiencias_longas: number | null;
  gap_meses_recente: number | null;
  padrao_carreira: string | null;
  observacoes_texto: string[];
}

export interface LinkedInExtracted {
  linkedin_url: string | null;
  nome_completo: string;
  headline: string | null;
  localizacao: string | null;
  resumo: string | null;
  idiomas: IdiomaItem[];
  experiencias: ExperienceItem[];
  skills: SkillItem[];
  formacao: FormacaoItem[];
  senioridade_global_inferida: string | null;
  anos_experiencia_total: number | null;
  areas_sugeridas: string[];
  carreira: CarreiraObservacoes | null;
  /**
   * Detecção de freelancer/empresa própria: true = sinais claros, false =
   * claramente empregado, null = ambíguo. A revisão pode confirmar/corrigir.
   */
  is_freelancer_declarado?: boolean | null;
  /** Evidências curtas em pt-BR pra tela de revisão. */
  sinais_freelancer?: string[];
  /** true quando o botão "É freelancer / Não é" foi respondido no apply. */
  vinculo_confirmado_humano?: boolean;
}

export interface JobMatchCandidate {
  freelancer_id: string;
  nome: string;
  email: string | null;
  foto_url: string | null;
  similarity_score: number;
  match_reason: string;
}

export interface DiffFieldChange {
  field: string;
  before: unknown;
  after: unknown;
}

/**
 * Diff entre a extração nova e o último LinkedIn aplicado. `fields` são mudanças
 * do perfil (geram selo na revisão); `*_internos` são campos que a IA infere ou
 * reescreve entre rodadas (só importam pro apply). Os campos `*_internos` e
 * `previous_applied_at` faltam em jobs anteriores à COR-367.
 */
export interface DiffSummary {
  has_changes: boolean;
  is_first: boolean;
  counts: {
    added: number;
    changed: number;
    changed_internos?: number;
    removed: number;
    fields_changed: number;
    fields_changed_internos?: number;
  };
  added: {
    experiencias: ExperienceItem[];
    skills: SkillItem[];
    formacao: FormacaoItem[];
  };
  changed: Array<{
    experiencia: ExperienceItem;
    before: ExperienceItem;
    fields: string[];
    fields_internos?: string[];
  }>;
  removed: {
    experiencias: ExperienceItem[];
    skills: SkillItem[];
    formacao: FormacaoItem[];
  };
  fields_changed: DiffFieldChange[];
  fields_changed_internos?: DiffFieldChange[];
  /** Data do apply anterior (timestamptz ISO) com que esta extração foi comparada. */
  previous_applied_at?: string | null;
}

export interface LinkedInUrlConflict {
  freelancer_id: string;
  nome: string;
  email: string | null;
}

/**
 * Resumo leve do perfil pra exibir na listagem (o backend deriva do
 * extracted_json e manda só isto — não o JSON inteiro). Mostra o "lugar atual".
 */
export interface JobFreelancerSummary {
  nome_completo: string | null;
  headline: string | null;
  empresa_atual: string | null;
  cargo_atual: string | null;
  is_atual_a_confirmar: boolean;
  /** Conclusão da IA: true = se declara freelancer hoje; null = ambíguo. */
  is_freelancer_declarado?: boolean | null;
  /** Meta de triagem rápida (a lista não manda o extracted_json inteiro). */
  linkedin_url?: string | null;
  senioridade?: string | null;
  anos_experiencia?: number | null;
  localizacao?: string | null;
}

/** Situação de trabalho respondida pela revisão por IA (review_adjudicator). */
export type SituacaoRevisaoIa = "empregado" | "freelancer" | "sem_trabalho";
export type ConfiancaRevisaoIa = "alta" | "media" | "baixa";
/** Por que a resposta da IA não bastou para aplicar (review_adjudicator.RejectionReason). */
export type RecusaRevisaoIa =
  | "resposta_fora_do_formato"
  | "confianca_nao_alta"
  | "empregado_sem_empresa"
  | "empresa_fora_das_atuais"
  | "sem_trabalho_com_posicao_atual";

/** Resposta da revisão por IA. Com `recusa`, o job ficou na revisão manual. */
export interface RevisaoIa {
  situacao?: SituacaoRevisaoIa;
  empresa_principal?: string | null;
  confianca?: ConfiancaRevisaoIa;
  evidencia?: string;
  recusa?: RecusaRevisaoIa;
}

/** Decisão do classificador de revisão automática vs manual (COR-333). */
export interface AutoReviewDecision {
  mode: "auto" | "manual";
  reasons: Array<{ code: string; detail: string }>;
  revisao_ia?: RevisaoIa;
}

export const SITUACAO_REVISAO_IA_LABELS: Record<SituacaoRevisaoIa, string> = {
  empregado: "Empregado",
  freelancer: "Freelancer ou empresa própria",
  sem_trabalho: "Sem trabalho atual",
};

export const CONFIANCA_REVISAO_IA_LABELS: Record<ConfiancaRevisaoIa, string> = {
  alta: "alta",
  media: "média",
  baixa: "baixa",
};

export const RECUSA_REVISAO_IA_LABELS: Record<RecusaRevisaoIa, string> = {
  resposta_fora_do_formato: "a resposta da IA veio fora do formato",
  confianca_nao_alta: "a confiança da IA não foi alta",
  empregado_sem_empresa: "a IA disse que está empregado, mas não apontou a empresa",
  empresa_fora_das_atuais: "a empresa apontada pela IA não é uma posição atual do perfil",
  sem_trabalho_com_posicao_atual: "a IA disse que está sem trabalho, mas o perfil tem posição atual",
};

/** Códigos de razão que indicam suspeita de freelancer/empresa própria. */
export const AUTO_REVIEW_FREELANCER_CODES = new Set([
  "sinal_freelancer",
  "empresa_propria",
  "multiplas_empresas_atuais",
]);

/** Rótulos pt-BR dos códigos do classificador (pra banner da revisão). */
export const AUTO_REVIEW_CODE_LABELS: Record<string, string> = {
  auto_apply_desligado: "Aplicação automática desligada",
  sinal_freelancer: "Sinais de atuação como freelancer",
  empresa_propria: "Possível empresa própria",
  multiplas_empresas_atuais: "Mais de uma empresa atual em paralelo",
  conflito_linkedin_url: "URL do LinkedIn pertence a outro freelancer",
  atual_a_confirmar: "Emprego atual precisa de confirmação",
  extracao_incompleta: "Extração incompleta",
  sem_criterio_auto: "Nenhum critério de aplicação automática atendido",
  sem_emprego_atual: "Sem emprego atual",
  empresa_reconhecida_ia: "Empresa atual reconhecida publicamente",
  empresa_cadastrada: "Empresa atual já cadastrada",
  freelancer_declarado: "Perfil se declara freelancer",
  revisao_ia: "Revisão por IA",
};

export function jobTemSuspeitaFreelancer(job: LinkedInJob): boolean {
  return (job.auto_review?.reasons ?? []).some((razao) =>
    AUTO_REVIEW_FREELANCER_CODES.has(razao.code),
  );
}

export interface LinkedInJob {
  id: string;
  batch_id: string | null;
  freelancer_id: string | null;
  status: LinkedInJobStatus;
  progress_pct: number;
  /** Origem do job: upload de PDF ou busca automática do perfil público. */
  source: "pdf_upload" | "scraping";
  s3_url: string | null;
  /** Foto e capa copiadas para o S3 (link assinado, 6h). Só em scraping. */
  photo_url?: string | null;
  cover_url?: string | null;
  /** Nome do arquivo (PDF) ou URL do perfil (busca automática). */
  original_filename: string;
  file_size_bytes: number | null;
  error_message: string | null;
  error_details: Record<string, unknown> | null;
  match_candidates: JobMatchCandidate[] | null;
  match_strategy: string | null;
  diff_summary: DiffSummary | null;
  previous_history_id: string | null;
  /** Dono atual de `extracted_json.linkedin_url` quando diverge de freelancer_id. */
  linkedin_url_conflict: LinkedInUrlConflict | null;
  extracted_at: string | null;
  applied_at: string | null;
  /** Proveniência do apply: 'human' (curadoria) ou 'auto' (IA). Null = não aplicado. */
  review_mode: "human" | "auto" | null;
  /** Usuário que aprovou (null quando aplicado automaticamente). */
  applied_by: string | null;
  /** Decisão do classificador auto/manual. Null = job anterior ao classificador. */
  auto_review: AutoReviewDecision | null;
  extracted_json: LinkedInExtracted | null;
  /** Resumo leve (nome/headline/lugar atual) pra listagem. Null enquanto processa. */
  summary: JobFreelancerSummary | null;
  created_at: string;
  updated_at: string;
}

/** Contagens globais do cabeçalho (GET /linkedin/resumo). */
export interface LinkedInResumo {
  review_ready: number;
  awaiting_match: number;
  /** Só as falhas que uma pessoa resolve. */
  failed: number;
  /** A Apify não respondeu (erro técnico ou limite): a busca tenta de novo sozinha. */
  aguardando_nova_busca: number;
  /** Perfis que a Apify não achou: se resolve corrigindo a URL na ficha. */
  nao_encontrados: number;
  processando: number;
  aplicados: number;
  sem_mudanca: number;
  pendente_mais_antigo: string | null;
  ultima_atualizacao: string | null;
}

/** O que entraria numa busca automática agora (GET /linkedin/scrape-preview). */
export interface LinkedInScrapePreview {
  elegiveis: number;
  nunca_buscados: number;
  ocupados: number;
  sem_url: number;
  busca_mais_antiga: string | null;
}

/** Linha agregada de um lote (GET /linkedin/batches). batch_id null = avulsos. */
export interface LinkedInBatchSummary {
  batch_id: string | null;
  /** Aplicados por critério de aprovação ('manual' = revisão humana). */
  aprovacoes: Array<{ code: string; count: number }>;
  source: "pdf_upload" | "scraping";
  total: number;
  review_ready: number;
  awaiting_match: number;
  failed: number;
  concluidos: number;
  processing: number;
  created_last: string | null;
}

export interface HistoryEntry {
  id: string;
  freelancer_id: string;
  extraction_job_id: string | null;
  s3_url: string | null;
  original_filename: string;
  extracted_json: LinkedInExtracted;
  applied_at: string | null;
  model_used: string;
  tokens_input: number | null;
  tokens_output: number | null;
  cost_estimate_cents: number | null;
  created_at: string;
}

export interface FreelancerLinkedInAtual {
  freelancer_id: string;
  history_id: string;
  nome_completo: string | null;
  headline: string | null;
  localizacao: string | null;
  resumo: string | null;
  linkedin_url: string | null;
  senioridade: string | null;
  anos_experiencia_total: number | null;
  carreira_observacoes: CarreiraObservacoes | null;
  areas_sugeridas: string[] | null;
  pdf_url: string | null;
  pdf_filename: string | null;
  open_to_work: boolean | null;
  open_to_work_observado_em: string | null;
  photo_url?: string | null;
  cover_url?: string | null;
  atualizado_em: string | null;
  model_used: string;
}

export const TERMINAL_STATUSES: LinkedInJobStatus[] = [
  "applied",
  "no_changes",
  "failed",
  "cancelled",
];

export function isJobTerminal(status: LinkedInJobStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}

export function statusLabel(status: LinkedInJobStatus): string {
  const map: Record<LinkedInJobStatus, string> = {
    pending: "Pendente",
    uploading: "Subindo PDF",
    extracting: "Extraindo texto",
    enriching: "Processando com IA",
    matching: "Procurando freelancer",
    awaiting_match: "Aguardando associação",
    diffing: "Comparando com versão anterior",
    no_changes: "Sem mudanças",
    review_ready: "Aguardando revisão",
    applied: "Aplicado",
    failed: "Falhou",
    cancelled: "Cancelado",
  };
  return map[status] ?? status;
}

export interface LinkedInSinal extends LinkedInTransition {
  id: string;
  freelancer_id: string;
  freelancer_nome: string;
  quente: boolean;
  observado_em: string;
  observado_antes_em: string | null;
}

export interface LinkedInSinaisResponse {
  items: LinkedInSinal[];
  total: number;
}
